import React, { useState, useEffect } from 'react';
import useDocumentMeta from '../../hooks/useDocumentMeta';
import useReducedMotion from '../../hooks/useReducedMotion';
import './StatementPage.css';

/* This page used to render its own <StaticTransition> and
   <StatementBackground> — but PageLayout, which wraps every route,
   already renders both. Every visit stacked two full-viewport noise
   canvases and ran two independent animation loops. StatementBackground
   was also a byte-identical copy of StaticBackground; it has been
   deleted. */
const StatementPage: React.FC = () => {
    const reduceMotion = useReducedMotion();
    const [isVisible, setIsVisible] = useState(reduceMotion);

    useDocumentMeta({
        title: 'Statement',
        description:
            'On found paper, unambitious creations, and the static noise that calms down when your hands are busy.'
    });

    useEffect(() => {
        if (reduceMotion) {
            setIsVisible(true);
            return;
        }
        const timer = setTimeout(() => setIsVisible(true), 400);
        return () => clearTimeout(timer);
    }, [reduceMotion]);

    return (
        <>
            <div className={`statement-page ${isVisible ? 'visible' : ''}`}>
                <article className="statement-content">
                    <h1 className="statement-title">Statement</h1>

                    <section className="statement-section">
                        <p className="statement-text">I've been told art statements are important.</p>
                        <p className="statement-text">I struggle to understand the importance of things.</p>
                        <p className="statement-text">I struggle to write this because I don't really know what to say about the things I do.</p>
                        <p className="statement-text">I struggle to understand where to put my focus.</p>
                        <p className="statement-text">Reflecting on the things I do feels very often a way to justify their existence, to rationalise their purpose and intent.</p>
                        <p className="statement-text">I struggle to understand how purposeful my actions can be.</p>
                        <p className="statement-text">And yet, sitting with my unambitious creations, caring for them, and giving them a place to inhabit makes me believe I can do beautiful things, makes me believe I can contribute, say things people might resonate with and enjoy the process in the meantime.</p>
                        <p className="statement-text">So I struggle less, I put these words together here and move on with my day.</p>
                    </section>

                    <section className="statement-section">
                        <h2 className="statement-subtitle">And then we got here</h2>
                        <p className="statement-text">It took me a while to understand that the creative process starts from a need, to deconstructing the silly idea that the art is the product.</p>
                        <p className="statement-text">The frustrations of not being able to actually create but only having ideas about potential creations was overwhelming at times, so I'd hide my notebook — embarrassed by my lack of talent — and would keep the ideas between me and myself.</p>
                        <p className="statement-text">And then I stumbled upon old books, maps, stray pages. It was fun to discover them. I started stacking them up.</p>
                        <p className="statement-text">With time I started cautiously putting them together, just to discover what my hands would guide me to do.</p>
                        <p className="statement-text">In those moments the static noise in my head calmed down. I kept going for hours.</p>
                        <p className="statement-text">Awed by this newfound joy I kept doing it; the artifacts on this site are the outcome of this process.</p>
                    </section>
                </article>
            </div>
        </>
    );
};

export default StatementPage;
